import React, { useState, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';

import { InputUI, FormErrorUI, FormLabelUI, CheckboxUI, AutoCompleteSelectUI, DatePickerUI } from '../../Interface';
import documentService from '../../../service/newDoc.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import { multiply } from 'mathjs';

const ComplaintCoreFields = ({ viewField, autocompleteOptions, autocompleteData }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  const [docs, setDocs] = useState([]);
  const [docsOptions, setDocsOptions] = useState([]);

  useEffect(() => {
    if (!values.companyId || !values.baseDocType) {
      setDocs([]);
      setDocsOptions([]);
      return;
    }

    if (values.companyId && values.baseDocType) {
      documentService
        .getDocLinesByTypeAndCompany({
          companyId: values.companyId,
          docTypeId: values.baseDocType,
        })
        .then((data) => {
          setDocs(data);
          const docOptions = generateAutocompleteItems(data, ['docNum', 'itemCode', 'itemName', 'docReference'], 'id');
          setDocsOptions(docOptions);
        });
    }
  }, [values.baseDocType, values.companyId]);

  const handleFieldChange = async (field, newVal) => {
    const selected = newVal?.value ?? null;
    await setFieldValue(field, selected);
    await validateField(field);
  };

  const removeVals = () => {
    setFieldValue('baseId', null);
    setFieldValue('baseLine', null);
    setFieldValue('baseDocnum', '');
    setFieldValue('reference', '');
    setFieldValue('itemCode', null);
    setFieldValue('itemName', '');
    setFieldValue('quantity', '');
    setFieldValue('lastPurchasePrice', '');
    setFieldValue('sumPurchasePrice', '');
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="col-span-2">
        <Field
          type="autocomplete"
          name="companyId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Partner" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.companyId} />}
          onChange={async (_e, newVal) => handleFieldChange('companyId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.companyId) }}
          selectedValue={values.companyId}
          selectedLabelValue={
            autocompleteOptions?.company?.find((item) => item.value === values.companyId)?.label || ''
          }
          dataset={autocompleteOptions?.company}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="baseDocType"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Bizonylat típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.baseDocType} />}
          onChange={async (_e, newVal) => handleFieldChange('baseDocType', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.baseDocType) }}
          selectedValue={values.baseDocType}
          selectedLabelValue={
            autocompleteOptions?.documentType.find((item) => item.value === values.baseDocType)?.label || ''
          }
          dataset={autocompleteOptions?.documentType}
        />
      </div>

      <div className="col-span-1">
        <Field
          name="baseDocnum"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Bizonylat szám" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.baseDocnum} />}
          InputLabelProps={{ shrink: Boolean(values.baseDocnum) }}
          disabled={true}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="reference"
          type="text"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Hivatkozás" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.reference} />}
          InputLabelProps={{ shrink: Boolean(values.reference) }}
          disabled={true}
        />
      </div>

      <div className="col-span-2">
        <Field
          name="itemCode"
          type="autocomplete"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cikk" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.itemCode} />}
          disabled={true}
          InputLabelProps={{ shrink: Boolean(values.itemCode) }}
          selectedValue={values.itemCode}
          selectedLabelValue={autocompleteOptions?.items?.find((item) => item.value === values.itemCode)?.label || ''}
          dataset={autocompleteOptions?.items}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="quantity"
          type="number"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Reklamált mennyiség" />}
          variant="standard"
          helperText={<FormErrorUI message={errors?.quantity} />}
          InputLabelProps={{ shrink: Boolean(values.quantity) }}
          disabled={viewField}
          onChange={(e) => {
            handleFieldChange('quantity', { value: e.target.value });
            handleFieldChange('sumPurchasePrice', {
              value: multiply(values.lastPurchasePrice || 0, e.target.value || 0) ?? '',
            });
          }}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="lastPurchasePrice"
          type="number"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Utolsó beszerzési ár" />}
          variant="standard"
          helperText={<FormErrorUI message={errors?.lastPurchasePrice} />}
          InputLabelProps={{ shrink: Boolean(values.lastPurchasePrice) }}
          disabled={true}
        />
      </div>

      <div className="col-span-1">
        <Field
          name="sumPurchasePrice"
          type="number"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Beszerzési ár összesen" />}
          variant="standard"
          helperText={<FormErrorUI message={errors?.sumPurchasePrice} />}
          InputLabelProps={{ shrink: Boolean(values.sumPurchasePrice) }}
          disabled={true}
        />
      </div>

      <div className="col-span-4">
        <Field
          name="baseId"
          type="autocomplete"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Bizonylat sor" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.baseId} />}
          onChange={async (_e, newVal) => {
            const selectedDocLine = docs.find((doc) => doc.id === newVal?.value);
            if (selectedDocLine) {
              await setFieldValue('baseId', selectedDocLine.docId);
              await setFieldValue('baseLine', selectedDocLine.lineNum);
              await setFieldValue('baseDocnum', selectedDocLine.docNum || '');
              await setFieldValue('reference', selectedDocLine.docReference || '');
              await setFieldValue('itemCode', selectedDocLine.itemCode || '');
              await setFieldValue('itemName', selectedDocLine.itemName || '');
              await setFieldValue('quantity', selectedDocLine.qty || 1);
              await setFieldValue('lastPurchasePrice', selectedDocLine.unitPrice || '');
              await setFieldValue(
                'sumPurchasePrice',
                multiply(selectedDocLine.unitPrice || 0, selectedDocLine.qty || 0) ?? ''
              );
            } else {
              removeVals();
            }
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.baseId) }}
          selectedValue={`${values.baseId}_${values.baseLine}`}
          selectedLabelValue={
            docsOptions?.find((item) => item.value === `${values.baseId}_${values.baseLine}`)?.label || ''
          }
          dataset={docsOptions}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="supplierId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Beszállító" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.supplierId} />}
          onChange={async (_e, newVal) => handleFieldChange('supplierId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.supplierId) }}
          selectedValue={values.supplierId}
          selectedLabelValue={
            autocompleteOptions?.company?.find((item) => item.value === values.supplierId)?.label || ''
          }
          dataset={autocompleteOptions?.company}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="date"
          component={DatePickerUI}
          value={values.date}
          fullWidth
          label={<FormLabelUI text="Dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.date} />}
          onChange={(date) => handleFieldChange('date', { value: date })}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.date) }}
        />
      </div>

      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="complaintStatusId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Állapot" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintStatusId} />}
          onChange={async (_e, newVal) => handleFieldChange('complaintStatusId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintStatusId) }}
          selectedValue={values.complaintStatusId}
          selectedLabelValue={
            autocompleteOptions?.complaintStatus.find((item) => item.value === values.complaintStatusId)?.label || ''
          }
          dataset={autocompleteOptions?.complaintStatus}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="complaintTypeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintTypeId} />}
          onChange={async (_e, newVal) => handleFieldChange('complaintTypeId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintTypeId) }}
          selectedValue={values.complaintTypeId}
          selectedLabelValue={
            autocompleteOptions?.complaintType.find((item) => item.value === values.complaintTypeId)?.label || ''
          }
          dataset={autocompleteOptions?.complaintType}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="operationId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Művelet" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.operationId} />}
          onChange={async (_e, newVal) => handleFieldChange('operationId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.operationId) }}
          selectedValue={values.operationId}
          selectedLabelValue={
            autocompleteOptions?.operation?.find((item) => item.value === values.operationId)?.label || ''
          }
          dataset={autocompleteOptions?.operationId}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="complaintEffectId"
          type="autocomplete"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Okozat" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintEffectId} />}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintEffectId) }}
          selectedValue={values.complaintEffectId}
          selectedLabelValue={
            autocompleteOptions?.complaintEffect?.find((item) => item.value === values.complaintEffectId)?.label || ''
          }
          dataset={autocompleteOptions?.complaintEffect}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="complaintReasonId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Reklamáció jellege" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintReasonId} />}
          onChange={async (_e, newVal) => handleFieldChange('complaintReasonId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintReasonId) }}
          selectedValue={values.complaintReasonId}
          selectedLabelValue={
            autocompleteOptions?.complaintReason.find((item) => item.value === values.complaintReasonId)?.label || ''
          }
          dataset={autocompleteOptions?.complaintReason}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="complaintResolutionRequestId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Rendezési igény" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintResolutionRequestId} />}
          onChange={async (_e, newVal) => handleFieldChange('complaintResolutionRequestId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintResolutionRequestId) }}
          selectedValue={values.complaintResolutionRequestId}
          selectedLabelValue={
            autocompleteOptions?.complaintResolutionRequest.find(
              (item) => item.value === values.complaintResolutionRequestId
            )?.label || ''
          }
          dataset={autocompleteOptions?.complaintResolutionRequest}
        />
      </div>
      <div className="col-span-1">
        <Field
          type="autocomplete"
          name="complaintResolutionId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Reklamáció végkimenetel" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintResolutionId} />}
          onChange={async (_e, newVal) => handleFieldChange('complaintResolutionId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintResolutionId) }}
          selectedValue={values.complaintResolutionId}
          selectedLabelValue={
            autocompleteOptions?.complaintResolution.find((item) => item.value === values.complaintResolutionId)
              ?.label || ''
          }
          dataset={autocompleteOptions?.complaintResolution}
        />
      </div>
      <div className="col-span-2">
        <Field
          type="autocomplete"
          name="complaintActionTakenId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Elbírálás eredménye" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.complaintActionTakenId} />}
          onChange={async (_e, newVal) => handleFieldChange('complaintActionTakenId', newVal)}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.complaintActionTakenId) }}
          selectedValue={values.complaintActionTakenId}
          selectedLabelValue={
            autocompleteOptions?.complaintActionTaken.find((item) => item.value === values.complaintActionTakenId)
              ?.label || ''
          }
          dataset={autocompleteOptions?.complaintActionTaken}
        />
      </div>
      <div className="col-span-1">
        <Field
          name="hasComplaintReport"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Készült hozzá 8D riport" />}
          helperText={<FormErrorUI message={errors.hasComplaintReport} />}
          disabled={viewField}
        />
      </div>
    </div>
  );
};

export default ComplaintCoreFields;
